import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useNotificationsPanel } from '@/hooks/useNotificationsPanel';
import { cn } from '@/lib/utils';

interface NotificationsPopoverProps {
  userId: string;
  viewAllPath: string;
}

export function NotificationsPopover({ userId, viewAllPath }: NotificationsPopoverProps) {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    formatTimeAgo,
    handleMarkAllAsRead,
    handleNotificationClick,
    handlePanelOpenChange,
  } = useNotificationsPanel(userId);

  return (
    <DropdownMenu onOpenChange={handlePanelOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-9 w-9 p-0" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
              {unreadCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex justify-between">
          <span>Notifications</span>
          <span className="text-xs text-gray-500">{unreadCount} unread</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-96 overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => void handleNotificationClick(notification)}
                className={cn(
                  'w-full border-b px-2 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50',
                  !notification.isRead && 'bg-blue-50 dark:bg-blue-900/20'
                )}
              >
                <p
                  className={cn(
                    'truncate font-medium',
                    !notification.isRead ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {notification.title}
                </p>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{notification.body}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatTimeAgo(notification.createdAt)}
                </p>
              </button>
            ))
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(viewAllPath)}>View all notifications</DropdownMenuItem>
        {unreadCount > 0 ? (
          <DropdownMenuItem onClick={() => void handleMarkAllAsRead()}>Mark all as read</DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
